import React, { useState, useEffect, useCallback, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import Header from "../../components/ui/Header";
import Sidebar from "../../components/ui/Sidebar";
import Icon from "../../components/AppIcon";
import DataTable from "./components/DataTable";
import Pagination from "./components/Pagination";
import Toast from "./components/Toast";
const apiUrl = import.meta.env.VITE_API_URL;

const UserBehaviour = () => {
  const defaultSort = { key: "createdAt", direction: "desc" };
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState([]);
  const [sortConfig, setSortConfig] = useState({ key: "createdAt", direction: "desc" });
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [totalPages, setTotalPages] = useState(0);
  const [toast, setToast] = useState({ show: false, message: "", type: "" });
  const searchTriggered = useRef(false);
  const searchClicked = useRef(false);
  const debounced = useRef(null);
  const [user, setUser] = useState(null);
  const [csvLoading, setCsvLoading] = useState(false);

  const columns = [
    { key: "sno", label: "Sno", sortable: false },
    { key: "pagePath", label: "Page Path", sortable: false },
    { key: "screenPageViews", label: "Screen Page Views", sortable: false },
    { key: "engagedSessions", label: "Engaged Sessions", sortable: false },
    { key: "averageSessionDuration", label: "Average Session Duration", sortable: false },
    { key: "bounceRate", label: "Bounce Rate", sortable: false },
    { key: "fetchedAt", label: "Fetched At", sortable: false }
  ];

  const fetchData = async () => {
    setIsLoading(true);
    try {
      const params = new URLSearchParams({
        search: searchQuery,
        page: currentPage,
        limit: itemsPerPage
      });

      const res = await fetch(`${apiUrl}/api/admin-user/google-analytics/user-behaviour?${params.toString()}`);
      const json = await res.json();

      if (res.ok) {
        setData(json.userBehaviours);
        setTotalPages(json.totalPages);
      } else {
        throw new Error(json.error || "Failed to fetch data.");
      }

      setError(null);
    } catch (err) {
      setError('Failed to fetch data.');
      setToast({ show: true, message: 'Failed to fetch data.', type: "error" });
    } finally {
      setIsLoading(false);
    }
  };

  // const downloadCsvData = async () => {
  //   setCsvLoading(true);
  //   try {
  //     const res = await fetch(`${apiUrl}/api/admin-user/csv`, {
  //       method: "POST",
  //       headers: {
  //         "Content-Type": "application/json",
  //       },
  //       body: JSON.stringify({
  //         search: searchQuery,
  //         page: currentPage,
  //         limit: itemsPerPage
  //       }),
  //     });

  //     if (!res.ok) throw new Error("CSV download failed");

  //     const blob = await res.blob();
  //     const url = window.URL.createObjectURL(blob);

  //     const a = document.createElement("a");
  //     a.href = url;

  //     const now = new Date();
  //     const dateStr = now.toISOString().split("T")[0]; // YYYY-MM-DD
  //     const timeStr = now
  //       .toTimeString()
  //       .split(" ")[0]
  //       .replace(/:/g, "");

  //     a.download = `users_${dateStr}_${timeStr}.csv`;
  //     a.click();
  //     window.URL.revokeObjectURL(url);

  //     setToast({ show: true, message: "CSV downloaded", type: "success" });
  //   } catch (err) {
  //     console.error(err);
  //     setToast({ show: true, message: "Failed to download CSV", type: "error" });
  //   } finally {
  //     setCsvLoading(false);
  //   }
  // };


  useEffect(() => {
    if (!data.length) return;

    const sorted = [...data].sort((a, b) => {
      const { key, direction } = sortConfig;
      const aVal = a[key]?.toString().toLowerCase() ?? "";
      const bVal = b[key]?.toString().toLowerCase() ?? "";

      if (aVal < bVal) return direction === "ascending" ? -1 : 1;
      if (aVal > bVal) return direction === "ascending" ? 1 : -1;
      return 0;
    });

    setData(sorted);
  }, [sortConfig]);

  useEffect(() => {
    if (!searchTriggered.current) {
      // fetchData();
    }
  }, [currentPage, itemsPerPage]);

  useEffect(() => {
    if (searchTriggered.current) {
      searchTriggered.current = false;
      // fetchData();
    }
  }, [searchTriggered.current])

  useEffect(() => {
    if (searchClicked.current) {
      debounced.current = setTimeout(() => {
        setCurrentPage(1);
        setSortConfig(defaultSort);
        setItemsPerPage(10);
        searchTriggered.current = true;
      }, 1000);
    }

    return () => clearTimeout(debounced.current);
  }, [searchQuery]);

  const requestSort = useCallback((key) => {
    let direction = "ascending";
    if (sortConfig.key === key && sortConfig.direction === "ascending") {
      direction = "descending";
    }
    setSortConfig({ key, direction });
  }, [sortConfig]);

  const handlePageChange = (pageNumber) => setCurrentPage(pageNumber);
  const handleItemsPerPageChange = (value) => {
    setItemsPerPage(value);
    setCurrentPage(1);
  };

  return (
    <div className="overflow-x-hidden">
      <div className="flex bg-background">
        <Sidebar
          variant="with-categories"
          onToggle={(collapsed) => setIsCollapsed(collapsed)}
        />
        <div className={`w-full transition-all duration-300 ${isCollapsed ? "lg:pl-16" : "lg:pl-64"}`}>
          <Header isCollapsed={isCollapsed} variant="default" />

          <main className="overflow-y-auto p-4 md:p-6 bg-background">
            <nav className="mb-4 text-sm" aria-label="Breadcrumb">
              <ol className="flex items-center space-x-1">
                <li>
                  <Link to="/analytics" className="text-gray-500 hover:text-primary-600">Analytics Data</Link>
                </li>
                <li className="flex items-center">
                  <Icon name="ChevronRight" size={16} className="text-gray-400 mx-1" />
                  <span className="text-gray-900 font-medium">User Behaviour List</span>
                </li>
              </ol>
            </nav>

            <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-6">
              <div>
                <h1 className="text-2xl font-bold text-gray-900">User Behaviour Management</h1>
                <p className="mt-1 text-sm text-gray-500">All registered User Behaviour from the system</p>
              </div>
            </div>


            <div className="mt-6 flex justify-center w-full h-full">
              <iframe className="w-full max-w-[1260px] h-[400px] overflow-hidden md:h-[700px]" src="https://lookerstudio.google.com/embed/reporting/a7d52a71-593a-418c-86a8-3c31d30a1233/page/8IESF" frameBorder="0" style={{ border: 0, overflow: 'hidden' }} allowFullScreen sandbox="allow-storage-access-by-user-activation allow-scripts allow-same-origin allow-popups allow-popups-to-escape-sandbox"></iframe>
            </div>

            {/* <div className="bg-white rounded-lg shadow-sm border border-gray-200"> */}
            {/* <div className="p-4 border-b w-full border-gray-200 flex flex-col md:flex-row md:items-center md:justify-between gap-2"> */}
            {/* <SearchInput
                  onClick={() => { searchClicked.current = true }}
                  onBlur={() => { searchClicked.current = false }}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onClear={() => { setSearchQuery(""); searchClicked.current = true }}
                  placeholder="Search users..."
                /> */}
            {/* <div className="flex md:flex-row flex-col gap-2 !ml-auto"> */}
            {/* <button               TODO:remove ml-auto class when search uncomment
                    onClick={downloadCsvData}
                    disabled={csvLoading}
                    className={`inline-flex whitespace-nowrap w-auto items-center gap-2 px-4 py-2 rounded-md text-sm transition bg-primary-600 hover:bg-primary-700 text-white`}
                  >
                    {csvLoading ? (
                      <Loader className="w-4 h-4 animate-spin" />
                    ) : (
                      <File className="w-4 h-4" />
                    )}
                    <span>Download CSV</span>
                  </button> */}

            {/* <div className="flex sm:flex-row sm:justify-between flex-col gap-2"> */}
            {/* <div className="flex items-center space-x-2"> */}
            {/* <span className="text-sm text-gray-500">Show:</span> */}
            {/* <select
                        className="form-select rounded-md border-gray-300 shadow-sm text-sm"
                        value={itemsPerPage}
                        onChange={(e) => handleItemsPerPageChange(Number(e.target.value))}
                        aria-label="Items per page"
                      >
                        <option value={10}>10</option>
                        <option value={25}>25</option>
                        <option value={50}>50</option>
                        <option value={100}>100</option>
                        <option value={500}>500</option>
                        <option value={1000}>1000</option>
                      </select> */}
            {/* </div> */}
            {/* </div> */}
            {/* </div>
              </div> */}

            {/* <DataTable
                columns={columns}
                data={data}
                isLoading={isLoading}
                error={error}
                sortConfig={sortConfig}
                onSort={requestSort}
                onRetry={fetchData}
                emptyMessage="No acquisition found."
                currentPage={currentPage}
                setUser={setUser}
                isCollapsed={isCollapsed}
                itemsPerPage={itemsPerPage}
              /> */}
            {/* 
              {!isLoading && !error && data.length > 0 && (
                <Pagination
                  currentPage={currentPage}
                  totalPages={totalPages}
                  onPageChange={handlePageChange}
                  totalItems={data.length}
                  itemsPerPage={itemsPerPage}
                  showingFrom={(currentPage - 1) * itemsPerPage + 1}
                  showingTo={Math.min(currentPage * itemsPerPage, data.length)}
                />
              )} */}
            {/* </div> */}
          </main>
        </div>

        {toast.show && (
          <Toast
            message={toast.message}
            type={toast.type}
            onClose={() => setToast({ ...toast, show: false })}
          />
        )}
        {/* {user &&
          <UserViewModal user={user} onClose={() => setUser(null)} />
        } */}
      </div>
    </div>
  );
};

export default UserBehaviour;